import React, { useCallback, useMemo } from 'react'
import { isNil } from 'ramda'
import {
  ComboboxProvider,
  ComboboxLabel,
  Combobox as BaseCombobox,
  ComboboxPopover,
  ComboboxItem,
  ComboboxCancel,
} from '@ariakit/react'

import {
  containerStyle,
  labelStyle,
  sublabelStyle,
  comboBoxStyle,
  leftMediaStyle,
  busyIconStyle,
  cancelBtnStyle,
} from './styles'
import {
  basicOptionStyle,
  optionSectionLabelStyle,
  optionStyle,
  popoverStyle,
} from '../../molecules/Select'
import { Icon, IconSize } from '../../atoms/Icon'
import { IconButton } from '../../molecules/IconButton'
import { TextFieldTypes } from '../../atoms/TextField/TextField.types'
import { InputHelperText } from '../../atoms/InputHelperText'
import { ComboboxControlledProps } from './ComboboxControlled.types'

// Main ComboboxControlled component
export const ComboboxControlled = React.forwardRef<
  HTMLInputElement,
  ComboboxControlledProps
>((props, ref) => {
  const {
    // Main controlled state props (aligned with ariakit naming)
    value,
    selectedValue,
    items = [],
    open,
    setValue,
    onSelect,
    onEnter,
    onOpenChange,
    onChange,
    onClear,
    showSearchPrompt = false,
    emptyState,
    emptyContent,
    resultsLabel,
    setValueOnClick = true,

    // UI and behavior props
    autoComplete = 'none',
    autoFocus,
    autoSelect = false,
    busy,
    hideBusyIcon,
    className = '',
    condensed,
    testId = '',
    disabled,
    error,
    helperText,
    id,
    label,
    leftMedia,
    name,
    onBlur,
    onFocus,
    onKeyDown,
    optionClass = '',
    optionTestId,
    placeholder,
    searchable,
    showSelectedSublabel,
    textRedacted,
    type = TextFieldTypes.TEXT,
    wrapperClass = '',
  } = props

  // For unique popover id
  const popoverId = useMemo(() => `${name}-popover`, [name])

  // Handle keyboard interactions
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Call parent handler first if provided
      if (onKeyDown) {
        onKeyDown(event)
        // If event was prevented, don't continue
        if (event.defaultPrevented) {
          return
        }
      }

      if (event.key === 'Enter') {
        if (!selectedValue && searchable && value) {
          if (onEnter) {
            // If searchable and no selection, use input as keyword search
            onEnter({ value: value, label: value })
          } else {
            handleSelectValue(value)
          }
        }

        // Close popover
        onOpenChange?.(false)
      }
    },
    [onKeyDown, selectedValue, searchable, value, onEnter, onOpenChange],
  )

  // Handle blur event
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // If no input value AND no selected value, reset selection
      // Only clear if we truly have nothing (prevents clearing after valid selection)
      if (!value && !selectedValue) {
        onSelect(undefined)
        setValue('')
      }

      // Call parent handler if provided
      onBlur?.(event)
    },
    [value, selectedValue, onSelect, setValue, onBlur],
  )

  // Handle item selection
  const handleSelectValue = useCallback(
    (valueStr: any) => {
      // Don't select empty values
      if (!valueStr) return

      // Handle keyword search or normal selection
      let selectedOption = valueStr
      if (typeof valueStr === 'string') {
        selectedOption =
          items.find(option => option.value.toString() === valueStr) || valueStr
      }

      // Update selected value
      onSelect?.(selectedOption)

      // Close popover
      onOpenChange?.(false)
    },
    [items, onSelect, onOpenChange],
  )

  // Handle input changes
  const handleChangeValue = useCallback(
    (newValue: string) => {
      // Update input value
      setValue(newValue)

      // Trigger onChange callback immediately if provided
      if (onChange) {
        // Call onChange but don't use the return value since we're fully controlled
        onChange(newValue)
      }
    },
    [setValue, onChange],
  )

  // Handle clearing the input
  const handleClear = useCallback(() => {
    onSelect?.(undefined)
    setValue('')
    onClear?.()
  }, [onSelect, setValue, onClear])

  // Compute class for the input field
  const inputClassName = useMemo(() => {
    return `${comboBoxStyle({
      condensed,
      hasError: !isNil(error),
      hasIcon: !isNil(leftMedia),
      hasValues: !isNil(selectedValue),
      showSelectedSublabel,
    })} ${className}`
  }, [
    condensed,
    error,
    leftMedia,
    selectedValue,
    showSelectedSublabel,
    className,
  ])

  // open has no default. ?? (nullish coalescing) uses the right side only when open is null/undefined (prop omitted),
  // so we can tell "uncontrolled" from "parent passed false". When omitted we open when there are items;
  // when open={false} we keep false so the dropdown can close on select. Defaulting open to false would break that.
  const effectiveOpen = open ?? (items.length > 0 || undefined)

  return (
    <div className={wrapperClass}>
      <ComboboxProvider
        open={effectiveOpen}
        {...(onOpenChange && { setOpen: onOpenChange })}
        // Pass '' (not undefined) when there is no selection. undefined makes
        // Ariakit treat selectedValue as uncontrolled and retain its last
        // internal value, so re-selecting the previously-selected option is a
        // no-op that never fires onSelect (BASH-317). '' keeps it controlled.
        selectedValue={selectedValue?.value?.toString() ?? ''}
        setSelectedValue={handleSelectValue}
        setValue={handleChangeValue}
        value={value || ''}
      >
        <div className={containerStyle}>
          {/* The actual input field - inlined from previous ComboboxInput */}
          <BaseCombobox
            ref={ref}
            aria-invalid={error ? true : null}
            aria-describedby={
              !!error || !!helperText
                ? `${error ? `${name}-error` : ''} ${
                    helperText ? `${name}-helper` : ''
                  }`
                : null
            }
            aria-controls={popoverId}
            aria-expanded={effectiveOpen}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            autoSelect={autoSelect}
            className={inputClassName}
            data-heap-redact-text={textRedacted}
            data-testid={testId || 'combobox-input'}
            disabled={disabled}
            id={id}
            name={name}
            placeholder={placeholder}
            type={type}
            onBlur={handleBlur}
            onChange={e => handleChangeValue(e.target.value)}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
            value={value || ''}
            setValueOnClick={false}
          />

          {/* Left media (icon) */}
          {!isNil(leftMedia) && (
            <div className={leftMediaStyle}>{leftMedia}</div>
          )}

          {/* Hide the label if showSelectedSublabel is true and there is a selected value */}
          {(!showSelectedSublabel || isNil(selectedValue)) && (
            <ComboboxLabel
              className={labelStyle({
                condensed: condensed,
                disabled: disabled || busy,
                hasError: !isNil(error),
                hasIcon: !isNil(leftMedia),
                hasValues: !isNil(selectedValue) || !isNil(value),
              })}
              data-testid="info_label"
            >
              {label}
            </ComboboxLabel>
          )}

          {/* Display sublabel if needed */}
          {showSelectedSublabel && !condensed && selectedValue?.sublabel && (
            <span
              className={sublabelStyle({
                condensed: condensed,
                hasIcon: !isNil(leftMedia),
              })}
              data-testid="info_comboboxSublabel"
            >
              {selectedValue.sublabel}
            </span>
          )}

          {/* Loading indicator. Consumers that show the loading state
              elsewhere (e.g. on a trailing submit button) pass hideBusyIcon so
              a single search doesn't spin two indicators. */}
          {busy && !hideBusyIcon && (
            <Icon
              name="loading"
              className={busyIconStyle(condensed)}
              size={condensed ? IconSize.SM : IconSize.MD}
              aria-label="Loading"
            />
          )}

          {/* Clear button */}
          {!busy && !disabled && (selectedValue || value) && (
            <ComboboxCancel
              onClick={handleClear}
              render={
                <IconButton
                  testId={`btn_clear_${testId}`}
                  icon="x"
                  size={IconSize.SM}
                  label="Clear"
                  className={cancelBtnStyle}
                  type="button"
                />
              }
            />
          )}
        </div>

        {/* Popover with options */}
        <ComboboxPopover
          id={popoverId}
          className={popoverStyle}
          gutter={4}
          sameWidth
          hideOnInteractOutside
        >
          {/* Search option for searchable combobox */}
          {searchable && !!value && (
            <ComboboxItem
              className={`${optionStyle} ${optionClass}`}
              key={`search-${value}`}
              value={value}
              onClick={() => {
                if (onEnter) {
                  onEnter({ value: value, label: value })
                } else {
                  handleSelectValue(value)
                }
                onOpenChange?.(false)
              }}
            >
              <div>Search for "{value}"</div>
            </ComboboxItem>
          )}

          {/* "Type to search" prompt - now controlled by showSearchPrompt prop */}
          {showSearchPrompt && !items.length && !value && !!onChange && (
            <div className={basicOptionStyle}>Type to begin searching...</div>
          )}

          {/* Empty-input content (e.g. recent searches) shown on focus before
              the member types. The parent owns the markup and open-on-focus. */}
          {!value && !busy && !isNil(emptyContent) && emptyContent}

          {/* Optional heading above the suggestion list */}
          {!!items.length && !!resultsLabel && (
            <p className={optionSectionLabelStyle}>{resultsLabel}</p>
          )}

          {/* Display suggestions */}
          {!!items.length &&
            items.map((option, index) => (
              <ComboboxItem
                className={`${optionStyle} ${optionClass}`}
                key={option.key || option.value?.toString()}
                value={option.value?.toString()}
                setValueOnClick={setValueOnClick}
                data-testid={
                  optionTestId ? `${optionTestId}_${index}` : undefined
                }
              >
                {option.leftMedia ? option.leftMedia : leftMedia}
                <div className="flex-grow">
                  {option.label}
                  {!isNil(option.sublabel) && (
                    <div className="sh-body-xs">{option.sublabel}</div>
                  )}
                </div>
                {option.rightMedia && option.rightMedia}
              </ComboboxItem>
            ))}

          {/* Empty / error state: non-selectable row shown when a query
              returned no items (and we're not mid-fetch). Parent owns the copy
              so "no results" vs "search failed" is its call. */}
          {!items.length && !busy && !!value && !isNil(emptyState) && (
            <div className={basicOptionStyle} role="status">
              {emptyState}
            </div>
          )}
        </ComboboxPopover>

        {/* Error and helper text */}
        <InputHelperText name={name} error={error} helperText={helperText} />
      </ComboboxProvider>
    </div>
  )
})

ComboboxControlled.displayName = 'ComboboxControlled'
